import { useQuery } from "@apollo/client";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import { blue, orange, red } from "@mui/material/colors";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { BarSeriesType } from "@mui/x-charts";
import { LineChart } from "@mui/x-charts/LineChart";
import { BarChart } from "components/Dashboard/BarChart";
import DonutChart from "components/Dashboard/DonutChart";
import { StatDisplay } from "components/StatDisplay";
import { HarmonizedViolationProgram, HarmonizedViolationSeverity } from "generated-graphql/graphql";
import { useFullOmnisearch } from "hooks/omnisearch-v2";
import { HARMONIZED_VIOLATION_STATS } from "hooks/useHarmonizedViolations";
import { ReactNode, useMemo, useState } from "react";
import { ProgramAreaColors } from "util/insights";

import {
  MetricInfoIcon,
  PROGRAM_EXPLANATION,
  SEVERITY_EXPLANATION,
  STALE_EXPLANATION,
} from "./violationDisplay";

// Softer, lower-saturation palette to match the other Insights tabs (and the
// gentler chip spec) — the full-intensity error/warning mains read as too harsh
// here. Severity stays semantic (red/amber/blue); program reuses the shared
// Insights program-area palette so colors are consistent app-wide.
const SEVERITY_DONUT_COLORS: Record<string, string> = {
  [HarmonizedViolationSeverity.High]: red[400],
  [HarmonizedViolationSeverity.Medium]: orange[400],
  [HarmonizedViolationSeverity.Low]: blue[300],
};

const PROGRAM_DONUT_COLORS: Record<string, string> = {
  [HarmonizedViolationProgram.Water]: ProgramAreaColors.Water,
  [HarmonizedViolationProgram.Air]: ProgramAreaColors.Air,
  [HarmonizedViolationProgram.Waste]: ProgramAreaColors.Waste,
};

type StatBucket = { key: string; label: string; count: number };

const FALLBACK_DONUT_COLOR = "#9e9e9e";

function donutData(buckets: StatBucket[], colors: Record<string, string>) {
  return buckets.map((b) => ({
    id: b.key,
    value: b.count,
    label: b.label,
    color: colors[b.key] ?? FALLBACK_DONUT_COLOR,
  }));
}

export function ViolationsOverviewPanel({ tenantId }: { tenantId: string }) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(true);
  const omnisearch = useFullOmnisearch();

  const { data, loading } = useQuery(HARMONIZED_VIOLATION_STATS, {
    variables: { tenantId, search: omnisearch },
    skip: !tenantId,
  });

  const stats = data?.harmonizedViolationStats;

  const severityDonutData = useMemo(
    () => donutData(stats?.bySeverity ?? [], SEVERITY_DONUT_COLORS),
    [stats?.bySeverity]
  );
  const severityDonutColors = useMemo(
    () =>
      (stats?.bySeverity ?? []).map((b) => SEVERITY_DONUT_COLORS[b.key] ?? FALLBACK_DONUT_COLOR),
    [stats?.bySeverity]
  );
  const programDonutData = useMemo(
    () => donutData(stats?.byProgram ?? [], PROGRAM_DONUT_COLORS),
    [stats?.byProgram]
  );
  const programDonutColors = useMemo(
    () => (stats?.byProgram ?? []).map((b) => PROGRAM_DONUT_COLORS[b.key] ?? FALLBACK_DONUT_COLOR),
    [stats?.byProgram]
  );
  const topFacilitiesDataset = useMemo(
    () =>
      (stats?.topFacilities ?? []).map((f) => ({
        facilityName: f.facilityName,
        count: f.count,
      })),
    [stats?.topFacilities]
  );
  const quarterTrendDataset = useMemo(
    () =>
      (stats?.byQuarter ?? []).map((q) => ({
        quarter: q.label,
        count: q.count,
      })),
    [stats?.byQuarter]
  );
  const assigneeDataset = useMemo(
    () =>
      (stats?.byAssignee ?? []).map((b) => ({
        label: b.label,
        count: b.count,
      })),
    [stats?.byAssignee]
  );
  const regionDataset = useMemo(
    () =>
      (stats?.byRegion ?? []).map((b) => ({
        label: b.label,
        count: b.count,
      })),
    [stats?.byRegion]
  );
  const businessUnitDataset = useMemo(
    () =>
      (stats?.byBusinessUnit ?? []).map((b) => ({
        label: b.label,
        count: b.count,
      })),
    [stats?.byBusinessUnit]
  );
  const barSeries = useMemo(
    () =>
      [
        {
          type: "bar",
          dataKey: "count",
          // Softer single hue (matches the Insights priority palette) rather
          // than the saturated brand primary.
          color: blue[400],
          valueFormatter: (value) => `Violations (${value})`,
        },
      ] satisfies BarSeriesType[],
    []
  );

  return (
    // No surrounding outline/fill: the page canvas is already light gray and the
    // metric tiles carry their own gray, so a panel border here would just be a
    // box around boxes.
    <Box sx={{ px: 2, py: 1 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h6">Violations overview</Typography>
        <Button
          size="small"
          variant="text"
          startIcon={expanded ? <ExpandLess /> : <ExpandMore />}
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded ? "Hide charts" : "Show charts"}
        </Button>
      </Stack>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ mt: 2 }}>
          {loading && !stats ? <OverviewSkeleton /> : null}
          {!loading && stats && stats.total === 0 ? (
            <Typography color="text.secondary">No violations yet</Typography>
          ) : null}
          {stats && stats.total > 0 ? (
            <Stack spacing={3}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ flexWrap: "wrap" }}>
                <KpiCard label="Total" value={stats.total} />
                <KpiCard
                  label="High severity"
                  value={stats.highSeverity}
                  color={theme.palette.error.main}
                />
                <KpiCard label="Unresolved" value={stats.unresolved} />
                <KpiCard
                  label="Stale"
                  value={stats.stale}
                  color={theme.palette.warning.main}
                  info={<MetricInfoIcon explanation={STALE_EXPLANATION} label="stale" />}
                />
                <KpiCard label="Facilities affected" value={stats.facilitiesAffected} />
              </Stack>

              <Stack direction={{ xs: "column", lg: "row" }} spacing={3}>
                <Stack sx={{ flex: 1 }}>
                  <ChartHeading
                    title="By severity"
                    info={<MetricInfoIcon explanation={SEVERITY_EXPLANATION} label="severity" />}
                  />
                  <DonutChart
                    seriesData={severityDonutData}
                    label={String(stats.total)}
                    subLabel="violations"
                    containerHeight="18rem"
                    legend={{ direction: "column", position: "right" }}
                    colors={severityDonutColors}
                  />
                </Stack>
                <Stack sx={{ flex: 1 }}>
                  <ChartHeading
                    title="By program"
                    info={<MetricInfoIcon explanation={PROGRAM_EXPLANATION} label="program" />}
                  />
                  <DonutChart
                    seriesData={programDonutData}
                    label={String(stats.total)}
                    subLabel="violations"
                    containerHeight="18rem"
                    legend={{ direction: "column", position: "right" }}
                    colors={programDonutColors}
                  />
                </Stack>
              </Stack>

              <Stack direction={{ xs: "column", lg: "row" }} spacing={3}>
                <Stack sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Top facilities
                  </Typography>
                  <Box sx={{ height: theme.spacing(34) }}>
                    <BarChart
                      dataset={topFacilitiesDataset}
                      series={barSeries}
                      xAxis={[
                        { scaleType: "band", dataKey: "facilityName", categoryGapRatio: 0.4 },
                      ]}
                      yAxis={[{ scaleType: "linear" }]}
                      noData={stats.topFacilities.length === 0}
                      noDataMessage="No facilities"
                    />
                  </Box>
                </Stack>
                <Stack sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    By quarter
                  </Typography>
                  <Box sx={{ height: theme.spacing(34) }}>
                    {/* A line chart reads as a trend over time, matching the
                        other Insights tabs (bars looked out of place here). */}
                    <LineChart
                      dataset={quarterTrendDataset}
                      series={[
                        {
                          dataKey: "count",
                          color: blue[400],
                          valueFormatter: (value) =>
                            value === null ? "" : `Violations (${value})`,
                        },
                      ]}
                      xAxis={[{ scaleType: "point", dataKey: "quarter" }]}
                      yAxis={[{ scaleType: "linear", min: 0 }]}
                      grid={{ horizontal: true }}
                      slotProps={{ legend: { hidden: true } }}
                    />
                  </Box>
                </Stack>
              </Stack>

              <Stack direction={{ xs: "column", lg: "row" }} spacing={3}>
                <Stack sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Open violations by Assignee
                  </Typography>
                  <Box sx={{ height: theme.spacing(34) }}>
                    <BarChart
                      dataset={assigneeDataset}
                      series={barSeries}
                      xAxis={[{ scaleType: "band", dataKey: "label", categoryGapRatio: 0.4 }]}
                      yAxis={[{ scaleType: "linear" }]}
                      noData={stats.byAssignee.length === 0}
                      noDataMessage="No data"
                    />
                  </Box>
                </Stack>
                <Stack sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Open violations by Region
                  </Typography>
                  <Box sx={{ height: theme.spacing(34) }}>
                    <BarChart
                      dataset={regionDataset}
                      series={barSeries}
                      xAxis={[{ scaleType: "band", dataKey: "label", categoryGapRatio: 0.4 }]}
                      yAxis={[{ scaleType: "linear" }]}
                      noData={stats.byRegion.length === 0}
                      noDataMessage="No data"
                    />
                  </Box>
                </Stack>
                <Stack sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Open violations by Business unit
                  </Typography>
                  <Box sx={{ height: theme.spacing(34) }}>
                    <BarChart
                      dataset={businessUnitDataset}
                      series={barSeries}
                      xAxis={[{ scaleType: "band", dataKey: "label", categoryGapRatio: 0.4 }]}
                      yAxis={[{ scaleType: "linear" }]}
                      noData={stats.byBusinessUnit.length === 0}
                      noDataMessage="No data"
                    />
                  </Box>
                </Stack>
              </Stack>
            </Stack>
          ) : null}
        </Box>
      </Collapse>
    </Box>
  );
}

/**
 * A metric tile: light gray fill, no outline, on the page's light gray canvas —
 * the established treatment (see TileContentGreyBackground on the waste facility
 * page). Outlined cards inside an outlined panel read as boxes on boxes.
 *
 * The fill is `background.paper` — the same surface the outlined Paper used. What
 * makes these read as tiles is the page canvas behind them, not a border, which
 * is the whole point: no outline, no boxes-on-boxes, still grouped.
 *
 * `grey50` looks like the obvious "light gray" token and is wrong in both modes.
 * In light it is #F9F9F9 against a #F3F3F3 canvas — *lighter* than the page, an
 * L* step of -2.1, so an unbordered tile reads as no tile at all. (The waste
 * page's `TileContentGreyBackground` gets away with grey50 only because it sits
 * on white.) In dark it inverts to encamp-gray[700] #545454, light enough that
 * StatDisplay's grey[500] label falls to 1.9:1.
 *
 * Going darker instead (encamp-gray[200]) gives the same L* +4.2 step but drags
 * every value down with it — the Stale figure lands at 2.52:1, under the 3:1
 * large-text floor. `background.paper` is the only option where all four values
 * clear it, and it is the best available for the 12px label (3.95:1; that label
 * is under AA on every surface, which is a StatDisplay issue, not this tile's).
 */
function KpiCard({
  label,
  value,
  color,
  info,
}: {
  label: string;
  value: number;
  color?: string;
  info?: ReactNode;
}) {
  return (
    <Box
      sx={{
        px: 2,
        py: 1,
        flex: 1,
        minWidth: 140,
        position: "relative",
        borderRadius: 1,
        bgcolor: "background.paper",
      }}
    >
      {info ? <Box sx={{ position: "absolute", top: 2, right: 2 }}>{info}</Box> : null}
      <StatDisplay label={label} value={value} valueProps={color ? { sx: { color } } : undefined} />
    </Box>
  );
}

/** A chart section heading with an info icon explaining the metric. */
function ChartHeading({ title, info }: { title: string; info: ReactNode }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
      <Typography variant="subtitle2">{title}</Typography>
      {info}
    </Stack>
  );
}

function OverviewSkeleton() {
  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={2}>
        {[0, 1, 2, 3, 4].map((i) => (
          <Skeleton key={i} variant="rounded" height={64} sx={{ flex: 1 }} />
        ))}
      </Stack>
      <Stack direction="row" spacing={3}>
        <Skeleton variant="rounded" height={200} sx={{ flex: 1 }} />
        <Skeleton variant="rounded" height={200} sx={{ flex: 1 }} />
      </Stack>
    </Stack>
  );
}
